import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CommonResponsesCtx = createContext(null);

export const CR_MIME = "application/x-auditflow-snippet";

export function CommonResponsesProvider({ children }) {
    const { user } = useAuth();
    const isConsultant = user?.role === "consultant";
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        if (!isConsultant) return;
        try {
            const { data } = await api.get("/firm/common-responses");
            setItems(data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setLoaded(true);
        }
    }, [isConsultant]);

    useEffect(() => {
        load();
    }, [load]);

    const create = async ({ title, body }) => {
        try {
            const { data } = await api.post("/firm/common-responses", { title, body });
            setItems((s) => [...s, data]);
            toast.success("Snippet saved");
            return data;
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
            return null;
        }
    };

    const update = async (id, patch) => {
        try {
            const { data } = await api.patch(`/firm/common-responses/${id}`, patch);
            setItems((s) => s.map((it) => (it.id === id ? data : it)));
            return data;
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
            return null;
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/firm/common-responses/${id}`);
            setItems((s) => s.filter((it) => it.id !== id));
            toast.success("Snippet removed");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    };

    const value = {
        isConsultant,
        items,
        loaded,
        open,
        setOpen,
        create,
        update,
        remove,
        reload: load,
    };
    return (
        <CommonResponsesCtx.Provider value={value}>
            {children}
        </CommonResponsesCtx.Provider>
    );
}

export function useCommonResponses() {
    const ctx = useContext(CommonResponsesCtx);
    if (!ctx) throw new Error("useCommonResponses outside provider");
    return ctx;
}

/**
 * Hook that returns props to spread on any <textarea> or <input> to make it a
 * drop target for Common Response snippets. Inserts the snippet body at the
 * current caret position (or appends if the element isn't focused).
 *
 * Usage:
 *   const dropProps = useSnippetDropTarget(value, setValue);
 *   <textarea value={value} onChange={...} {...dropProps} />
 */
export function useSnippetDropTarget(value, setValue) {
    const [drag, setDrag] = useState(false);

    const insertAtCaret = (el, text) => {
        const start = el.selectionStart ?? (value?.length || 0);
        const end = el.selectionEnd ?? start;
        const cur = value || "";
        const next = cur.slice(0, start) + text + cur.slice(end);
        setValue(next);
        // Restore caret AFTER React re-renders.
        requestAnimationFrame(() => {
            const caret = start + text.length;
            try {
                el.focus();
                el.setSelectionRange(caret, caret);
            } catch {
                // caret restore is best-effort
            }
        });
    };

    return {
        onDragOver: (e) => {
            if (e.dataTransfer?.types?.includes(CR_MIME)) {
                e.preventDefault();
                setDrag(true);
            }
        },
        onDragLeave: () => setDrag(false),
        onDrop: (e) => {
            const payload = e.dataTransfer?.getData(CR_MIME);
            if (!payload) return;
            e.preventDefault();
            setDrag(false);
            insertAtCaret(e.currentTarget, payload);
        },
        "data-snippet-drop": drag ? "on" : "off",
    };
}
